/**
 * ScoreHistoryChart — simple inline SVG line chart for score history.
 *
 * Plots accessibility score (0–100) over time.
 * No external charting library — drawn with inline SVG.
 */

interface ScoreHistoryEntry {
  runId: string;
  scoreValue: number;
  scoreBand: string;
  completedAt: string;
}

interface Props {
  history: ScoreHistoryEntry[];
}

const VIEW_W = 400;
const VIEW_H = 120;
const PAD_X = 16;
const PAD_Y = 16;

function bandColor(band: string): string {
  switch (band) {
    case "excellent":
    case "strong":
      return "#22c55e";
    case "good":
      return "#3b82f6";
    case "fair":
    case "needs_improvement":
    case "accessibility_risk":
      return "#f97316";
    case "poor":
    case "high_risk":
      return "#ef4444";
    default:
      return "#6b7280";
  }
}

export function ScoreHistoryChart({ history }: Props) {
  if (history.length < 2) {
    return <p className="text-muted text-sm">Not enough data yet.</p>;
  }

  const plotW = VIEW_W - PAD_X * 2;
  const plotH = VIEW_H - PAD_Y * 2;

  // Map score 0–100 to Y coordinate (top = high score)
  function toY(score: number): number {
    return PAD_Y + plotH - (score / 100) * plotH;
  }

  // Distribute points evenly across X axis
  function toX(index: number): number {
    return PAD_X + (index / (history.length - 1)) * plotW;
  }

  const color = bandColor(history[history.length - 1]!.scoreBand);

  const points = history.map((entry, i) => ({
    x: toX(i),
    y: toY(entry.scoreValue),
    entry,
  }));

  const polylinePoints = points.map((p) => `${p.x},${p.y}`).join(" ");

  const lastPoint = points[points.length - 1]!;
  const lastScore = history[history.length - 1]!.scoreValue;
  // Position label: if near right edge, shift left; otherwise keep right
  const labelX = lastPoint.x + 6;
  const labelAnchor = "start";

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      width="100%"
      height="120"
      aria-label="Score history chart"
      role="img"
    >
      {/* Baseline guide at score 0 and 100 */}
      <line
        x1={PAD_X}
        y1={PAD_Y}
        x2={VIEW_W - PAD_X}
        y2={PAD_Y}
        stroke="#e5e7eb"
        strokeWidth="1"
      />
      <line
        x1={PAD_X}
        y1={VIEW_H - PAD_Y}
        x2={VIEW_W - PAD_X}
        y2={VIEW_H - PAD_Y}
        stroke="#e5e7eb"
        strokeWidth="1"
      />

      {/* Score line */}
      <polyline
        points={polylinePoints}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinejoin="round"
        strokeLinecap="round"
      />

      {/* Data point circles */}
      {points.map((p) => (
        <circle
          key={p.entry.runId}
          cx={p.x}
          cy={p.y}
          r="4"
          fill={color}
          stroke="white"
          strokeWidth="1.5"
        />
      ))}

      {/* Last value label */}
      <text
        x={labelX}
        y={lastPoint.y + 4}
        fill={color}
        fontSize="11"
        fontFamily="sans-serif"
        textAnchor={labelAnchor}
        fontWeight="600"
      >
        {Math.round(lastScore)}
      </text>
    </svg>
  );
}
